/**
 * `narrationResay.ts` — recognises a reply that is a workflow step's narration
 * said AGAIN. Web-only; Unity has no `AssistantText` origin to correct.
 *
 * A step's own narration is labelled `script` by the `response.create` that
 * asks for it (`RealtimeSession` → `AssistantText`), so the chat and the saved
 * transcript leave it out: the step panel already prints it. The same words
 * can also come back in a reply nothing tagged:
 *   - **"Say that again."** The Guide is told to repeat a narration line word
 *     for word, with no tool (`guide.md`, "Say that again").
 *   - **A narration that was talked over.** Server VAD cancels the say's
 *     response, truncates it to the audio actually played, and creates the next
 *     response itself, untagged. Holding an `INTERNAL SAY:` it never finished,
 *     the model often finishes it in that reply. A phone hearing its own
 *     loudspeaker does this on a loop (Spark trace of 2026-10-02: "First" →
 *     the step's instructions again → "First" → …).
 *
 * Either way the reply is the author's narration, verbatim (the `INTERNAL SAY:`
 * contract), so it is shown and saved like an answer the step panel already
 * carries: redundant (user direction, 2026-10-02). A cause-based tag cannot
 * catch it, because its cause is the MODEL's choice and nothing on the wire
 * records that choice. Its words do, and the verbatim contract is what makes
 * comparing them reliable. That is the only reason this is safe as a text
 * rule. The 2026-08-29 attribution arm that was reverted as language-unsafe
 * guessed at MEANING from prose; this compares the model's words with the words
 * it was told to repeat, which holds in any language.
 *
 * What counts, in either of two ways:
 *   - **Any reply:** its words are the opening words of one narration line
 *     spoken in this conversation, at least {@link K_MIN_RESAY_TOKENS} of them
 *     (or the whole line, if it is shorter). "Say that again" lands here.
 *   - **The reply right after a line was CUT OFF** (its response cancelled):
 *     its words are any stretch of that line, however short. Measured
 *     2026-10-02 on "Top Cover" cut after 0.7 s by a spoken "First.": besides
 *     restarting the line (4 of 12), the model answered with just "First," (2)
 *     or carried on from the cut, "remove the Top Cover." (1). Both read as an
 *     answer by the first rule; the cut is the cause that says they are not.
 * A reply that goes on past the line, or opens with anything else ("Sure!
 * First, remove…"), is an answer and is shown whole. Splitting one reply into
 * narration and answer is not attempted.
 *
 * ⚠ The known cost: an answer that QUOTES a narration's opening words (or, just
 * after a cut, any of its words) is treated as narration too. It is still
 * spoken; only the chat and the saved transcript leave it out, and the step
 * panel is showing those words already.
 */

/**
 * The shortest reply that can count as a re-said line. One or two words are
 * too easily a real answer that happens to open the same way: "Okay." against
 * a line reading "Okay, now lift the cover off."
 */
const K_MIN_RESAY_TOKENS = 3;

/**
 * How many narration lines are remembered. The model can only repeat a line it
 * has seen, which is a line of THIS conversation, and a long run speaks a few
 * per step. The bound stops a long session growing the list.
 */
const K_MAX_REMEMBERED_LINES = 32;

/**
 * The prefixes the base prompt's Internal Speech Messages contract defines,
 * including the language-tagged form (`internalSay.ts`). A tagged line is
 * spoken TRANSLATED, so a re-say of it does not match the stored words and
 * shows as an answer: the known cost of narrating in another language than
 * the step panel's.
 */
const K_SAY_PREFIX = /^\s*(?:INTERNAL|SCENE) SAY(?: \(in [^)]*\):\s*\[[^\]]*\]|:)\s*/;

/**
 * - `resay`: the reply is a narration line said again.
 * - `pending`: so far the reply could still be one — wait for more text.
 * - `answer`: it is not.
 */
export type NarrationResayVerdict = "resay" | "pending" | "answer";

/**
 * Comparison form: lowercase runs of letters and digits. Punctuation, line
 * breaks, apostrophe and quote styles are what a transcript may render
 * differently from the line that was sent ("I’m" and "I'm" both read `i m`);
 * the words are what the contract fixes.
 */
export function narrationTokens(text: string): string[] {
  return text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
}

/** The line a say message asks the model to speak, without its prefix. */
export function sayMessageLine(message: string): string {
  return message.replace(K_SAY_PREFIX, "");
}

/**
 * True when `reply` opens `line`. The reply's LAST token may be a fragment of
 * the line's token at that position: a streamed transcript splits words
 * ("Rem" + "ove"), and a reply cut off by a barge-in can end mid-word.
 */
function opensLine(reply: readonly string[], line: readonly string[]): boolean {
  if (reply.length === 0 || reply.length > line.length) return false;
  const last = reply.length - 1;
  for (let i = 0; i < last; i++) {
    if (reply[i] !== line[i]) return false;
  }
  return line[last]!.startsWith(reply[last]!);
}

/** True when `reply` is a stretch of `line` starting at any word. */
function isStretchOf(reply: readonly string[], line: readonly string[]): boolean {
  for (let start = 0; start + reply.length <= line.length; start++) {
    if (opensLine(reply, line.slice(start))) return true;
  }
  return false;
}

export class NarrationResayMatcher {
  private readonly m_lines: string[][] = [];
  /** The line most recently spoken, or said again: what a cut-off cut. */
  private m_lastLine: string[] | null = null;
  /** The line whose delivery was just cut off, until the next reply settles. */
  private m_cutLine: string[] | null = null;

  /** Whether any narration has been spoken; when not, nothing can match. */
  get hasLines(): boolean {
    return this.m_lines.length > 0;
  }

  /** Remember one narration line, as sent in its say message. */
  remember(message: string): void {
    const tokens = narrationTokens(sayMessageLine(message));
    if (tokens.length === 0) return;
    this.m_lines.push(tokens);
    if (this.m_lines.length > K_MAX_REMEMBERED_LINES) this.m_lines.shift();
    this.m_lastLine = tokens;
    this.m_cutLine = null;
  }

  /** The narration being spoken was cut off (its response was cancelled). */
  noteCutOff(): void {
    this.m_cutLine = this.m_lastLine;
  }

  /** A reply has been decided; a cut-off only explains the reply right after it. */
  noteReplySettled(): void {
    this.m_cutLine = null;
  }

  /**
   * Classify a reply's text so far. `final` is true once the reply is complete:
   * the text cannot grow, so a reply that still matches is decided on its
   * length. A final `resay` also makes that line the one a later cut-off cuts.
   */
  classify(text: string, final: boolean): NarrationResayVerdict {
    if (this.m_lines.length === 0) return "answer";
    const reply = narrationTokens(text);
    if (reply.length === 0) return final ? "answer" : "pending";
    let couldBe = false;
    const cut = this.m_cutLine;
    if (cut && isStretchOf(reply, cut)) {
      if (final) return this.resaid(cut);
      couldBe = true;
    }
    for (const line of this.m_lines) {
      if (!opensLine(reply, line)) continue;
      couldBe = true;
      if (final && reply.length >= Math.min(K_MIN_RESAY_TOKENS, line.length)) return this.resaid(line);
    }
    return couldBe && !final ? "pending" : "answer";
  }

  private resaid(line: string[]): NarrationResayVerdict {
    this.m_lastLine = line;
    return "resay";
  }
}
